// Hook + provider per la modalità kiosk Audiq (Sprint 7).
// La chat parte in kiosk mode se `/api/branding` ritorna kiosk_mode_enabled=true,
// override via query param ?kiosk=true|false per testing admin.

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBranding, type Branding } from './api';

interface KioskContextValue {
  enabled: boolean;
  idleResetSeconds: number;
  welcomeMessage: string | null;
  branding: Branding | null;
}

const KioskContext = createContext<KioskContextValue>({
  enabled: false,
  idleResetSeconds: 30,
  welcomeMessage: null,
  branding: null,
});

function readQueryOverride(): boolean | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const v = params.get('kiosk');
  if (v === 'true') return true;
  if (v === 'false') return false;
  return null;
}

export function KioskProvider({ children }: { children: React.ReactNode }) {
  const brandingQuery = useQuery({
    queryKey: ['branding'],
    queryFn: getBranding,
    staleTime: 5 * 60_000,
  });

  const override = readQueryOverride();
  const enabled = override !== null ? override : Boolean(brandingQuery.data?.kiosk_mode_enabled);
  const idleResetSeconds = brandingQuery.data?.kiosk_idle_reset_seconds ?? 30;
  const welcomeMessage = brandingQuery.data?.kiosk_welcome_message ?? null;

  const value = useMemo<KioskContextValue>(
    () => ({
      enabled,
      idleResetSeconds,
      welcomeMessage,
      branding: brandingQuery.data ?? null,
    }),
    [enabled, idleResetSeconds, welcomeMessage, brandingQuery.data],
  );

  // Side-effects per kiosk mode:
  //  - lock back navigation (push state intercept)
  //  - disable gestures (zoom)
  //  - hint dello user-select disabled (gestito via classe CSS body)
  useEffect(() => {
    if (!enabled) return;
    document.body.classList.add('audiq-kiosk');

    // Anti-back
    window.history.pushState(null, '', window.location.href);
    const onPop = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', onPop);

    // Disable gesture-based zoom su iOS / Android
    const onGesture = (e: Event) => e.preventDefault();
    document.addEventListener('gesturestart', onGesture);
    document.addEventListener('gesturechange', onGesture);
    document.addEventListener('gestureend', onGesture);

    // No context menu (long-press)
    const onContext = (e: MouseEvent) => e.preventDefault();
    document.addEventListener('contextmenu', onContext);

    // Anti-zoom keyboard (cmd+/-)
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && ['+', '-', '=', '0'].includes(e.key)) {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.classList.remove('audiq-kiosk');
      window.removeEventListener('popstate', onPop);
      document.removeEventListener('gesturestart', onGesture);
      document.removeEventListener('gesturechange', onGesture);
      document.removeEventListener('gestureend', onGesture);
      document.removeEventListener('contextmenu', onContext);
      window.removeEventListener('keydown', onKey);
    };
  }, [enabled]);

  return <KioskContext.Provider value={value}>{children}</KioskContext.Provider>;
}

export function useKiosk(): KioskContextValue {
  return useContext(KioskContext);
}

/**
 * Richiede fullscreen al primo gesture utente. Da chiamare dentro un onClick.
 * Su iOS Safari fullscreen API non funziona — fallback silenzioso (la PWA
 * standalone fa già il fullscreen via "Aggiungi a Home").
 */
export async function requestKioskFullscreen(): Promise<void> {
  if (!document.documentElement.requestFullscreen) return;
  try {
    await document.documentElement.requestFullscreen();
  } catch {
    /* noop su Safari iOS */
  }
}
